import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Users, ShoppingBag, TrendingUp, ChevronRight } from "lucide-react";
import { getAdminSlipOverview } from "@/lib/api";

const card = "rounded-2xl border border-gray-200 bg-white dark:border-white/[0.08] dark:bg-charcoal-surface shadow-sm";

// Compact owner-facing teaser — full breakdown lives at /admin (AdminDashboard).
export default function AnalyticsSummary() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { getAdminSlipOverview().then(setData).catch((e) => console.error("slip overview failed (ignored):", e)); }, []);

  const subs = data?.subscriptions?.distribution || {};
  const tiles = [
    { id: "users", icon: Users, label: "Total Users", value: data?.subscriptions?.total_users ?? 0 },
    { id: "deals-checked", icon: ShoppingBag, label: "Deals Checked", value: data?.snaggedify_metrics?.total_checks ?? 0 },
    { id: "conversion", icon: TrendingUp, label: "Purchase Conversion", value: `${data?.purchase_conversion?.conversion_rate_pct ?? 0}%` },
    { id: "vault-saves", icon: BarChart3, label: "Vault Saves", value: data?.snaggedify_metrics?.total_saves ?? 0 },
  ];

  return (
    <div className={`${card} mt-8 overflow-hidden`} data-testid="analytics-summary">
      <div className="flex items-center gap-2 border-b border-gray-100 dark:border-white/10 px-6 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-yellow to-brand-orange">
          <BarChart3 className="h-5 w-5 text-black" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-gray-900 dark:text-white">Analytics Summary</h3>
          <p className="text-[11px] text-gray-400">Live Slip metrics at a glance</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 p-4">
        {tiles.map((t) => (
          <div key={t.id} data-testid={`summary-tile-${t.id}`} className="rounded-xl bg-gray-50 dark:bg-white/5 p-4">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-zinc-400">
              <t.icon className="h-3.5 w-3.5 text-brand-orange" /> {t.label}
            </div>
            <p className="mt-1 text-2xl font-bold tracking-tighter text-gray-900 dark:text-white" data-testid={`summary-value-${t.id}`}>{t.value}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2 border-t border-gray-100 dark:border-white/10 p-4" data-testid="summary-plan-distribution">
        {["free", "plus", "pro", "gold"].map((p) => (
          <div key={p} className="rounded-lg bg-gray-50 dark:bg-white/5 py-2 text-center">
            <p className="text-sm font-black text-gray-900 dark:text-white">{subs[p] ?? 0}</p>
            <p className="text-[9px] font-bold uppercase text-gray-400">{p}</p>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => navigate("/admin")} data-testid="open-full-analytics-btn" className="flex w-full items-center gap-3 border-t border-gray-100 dark:border-white/10 px-6 py-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/5">
        <span className="text-sm font-bold text-gray-900 dark:text-white">Open Full Analytics Dashboard</span>
        <ChevronRight className="ml-auto h-5 w-5 text-gray-400" />
      </button>
    </div>
  );
}
